import { Link } from 'react-router-dom';
import { ArrowRightIcon, ShieldCheckIcon, BoltIcon, CheckCircleIcon, WalletIcon, UsersIcon, ChartBarIcon, GlobeAltIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

// Original 12 wallets from flashdoom.io with exact brand styling
const wallets = [
  {
    name: 'Binance',
    color: 'from-yellow-400 to-yellow-600',
    bgColor: 'bg-gradient-to-br from-yellow-500/30 to-yellow-600/20',
    textColor: 'text-yellow-400',
    icon: 'B',
    iconBg: 'linear-gradient(135deg, #F0B90B, #F9D65C)',
  },
  {
    name: 'Coinbase',
    color: 'from-blue-400 to-blue-600',
    bgColor: 'bg-gradient-to-br from-blue-500/30 to-blue-600/20',
    textColor: 'text-blue-400',
    icon: 'C',
    iconBg: 'linear-gradient(135deg, #0052FF, #6690FF)',
  },
  {
    name: 'Bybit',
    color: 'from-gray-300 to-gray-500',
    bgColor: 'bg-gradient-to-br from-gray-400/30 to-gray-600/20',
    textColor: 'text-gray-300',
    icon: '⟁',
    iconBg: 'linear-gradient(135deg, #333, #666)',
  },
  {
    name: 'Trust Wallet',
    color: 'from-blue-400 to-blue-600',
    bgColor: 'bg-gradient-to-br from-blue-500/30 to-blue-600/20',
    textColor: 'text-blue-400',
    icon: 'T',
    iconBg: 'linear-gradient(135deg, #3375BB, #5A9BD5)',
  },
  {
    name: 'Cash App',
    color: 'from-green-400 to-green-600',
    bgColor: 'bg-gradient-to-br from-green-500/30 to-green-600/20',
    textColor: 'text-green-400',
    icon: '$',
    iconBg: 'linear-gradient(135deg, #00D632, #00FF55)',
  },
  {
    name: 'PayPal',
    color: 'from-blue-500 to-blue-700',
    bgColor: 'bg-gradient-to-br from-blue-600/30 to-blue-800/20',
    textColor: 'text-blue-500',
    icon: 'P',
    iconBg: 'linear-gradient(135deg, #003087, #009CDE)',
  },
  {
    name: 'MetaMask',
    color: 'from-orange-400 to-orange-600',
    bgColor: 'bg-gradient-to-br from-orange-500/30 to-orange-600/20',
    textColor: 'text-orange-400',
    icon: 'M',
    iconBg: 'linear-gradient(135deg, #FF9900, #FFB347)',
  },
  {
    name: 'Blockchain',
    color: 'from-red-400 to-yellow-500',
    bgColor: 'bg-gradient-to-br from-red-500/30 to-yellow-500/20',
    textColor: 'text-red-400',
    icon: 'B',
    iconBg: 'linear-gradient(135deg, #EB001B, #F59E0B)',
  },
  {
    name: 'OKX Wallet',
    color: 'from-indigo-400 to-purple-500',
    bgColor: 'bg-gradient-to-br from-indigo-500/30 to-purple-500/20',
    textColor: 'text-indigo-400',
    icon: 'O',
    iconBg: 'linear-gradient(135deg, #0052FF, #7B61FF)',
  },
  {
    name: 'Crypto.com',
    color: 'from-purple-400 to-purple-600',
    bgColor: 'bg-gradient-to-br from-purple-500/30 to-purple-600/20',
    textColor: 'text-purple-400',
    icon: 'Ð',
    iconBg: 'linear-gradient(135deg, #7B61FF, #A89DFF)',
  },
  {
    name: 'GCash',
    color: 'from-green-400 to-green-600',
    bgColor: 'bg-gradient-to-br from-green-500/30 to-green-600/20',
    textColor: 'text-green-400',
    icon: 'G',
    iconBg: 'linear-gradient(135deg, #00B900, #00FF00)',
  },
  {
    name: 'Luno',
    color: 'from-yellow-400 to-yellow-600',
    bgColor: 'bg-gradient-to-br from-yellow-500/30 to-yellow-600/20',
    textColor: 'text-yellow-400',
    icon: 'L',
    iconBg: 'linear-gradient(135deg, #FFB800, #FFD700)',
  },
];

// Stats from original flashdoom.io
const stats = [
  { number: '75.8M+', label: 'Emails Sent' },
  { number: '99.9%', label: 'Uptime' },
  { number: '15+', label: 'Wallets' },
];

const features = [
  {
    name: 'Multi-Wallet Support',
    description: 'Send emails through Binance, Coinbase, Bybit, Trust Wallet, CashApp, PayPal, and more.',
    icon: WalletIcon,
  },
  {
    name: 'Lightning Fast',
    description: 'Powered by Cloudflare Edge for sub-100ms response times worldwide.',
    icon: BoltIcon,
  },
  {
    name: 'Secure & Private',
    description: 'Enterprise-grade security with encrypted data and GDPR compliance.',
    icon: ShieldCheckIcon,
  },
  {
    name: '100% Free Forever',
    description: 'No hidden fees, no credit limits, no payment required. Everything is free!',
    icon: CheckCircleIcon,
  },
];

function WalletLogo({ wallet, size = 'md' }: { wallet: typeof wallets[0]; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = {
    sm: 'w-10 h-10 text-lg',
    md: 'w-14 h-14 text-2xl',
    lg: 'w-16 h-16 text-3xl',
  };

  return (
    <div
      className={`${sizeClasses[size]} rounded-2xl flex items-center justify-center font-bold`}
      style={{ background: wallet.iconBg }}
    >
      <span className="text-white">{wallet.icon}</span>
    </div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-dark-900">
      {/* Header - Original Design */}
      <header className="fixed inset-x-0 top-0 z-50 bg-dark-900/85 backdrop-blur-xl border-b border-primary-400/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center">
                <span className="text-dark-900 font-black text-xl">F</span>
              </div>
              <span className="text-2xl font-black text-primary-400 tracking-tight">FLASHDOOM</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/login" className="px-4 py-2 text-gray-300 hover:text-primary-400 font-medium transition-colors rounded-lg hover:bg-primary-400/10">
                Login
              </Link>
              <Link to="/register" className="px-5 py-2.5 bg-gradient-to-r from-primary-400 to-primary-600 text-dark-900 font-semibold rounded-xl hover:shadow-lg hover:shadow-primary-400/30 transition-all hover:-translate-y-0.5">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero - Original Design */}
      <section className="relative min-h-screen flex flex-col justify-center items-center text-center px-6 pt-20 pb-16 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-radial from-primary-400/10 via-transparent to-transparent animate-pulse" />
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900" />

        <div className="relative max-w-5xl mx-auto">
          {/* V1.1 Badge - Original */}
          <div className="inline-flex items-center gap-2 bg-primary-400/10 border border-primary-400/30 px-5 py-2 rounded-full text-primary-400 text-sm font-medium mb-8 animate-fade-in">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            V1.1 - Multi Crypto Wallet Mailer
          </div>

          {/* Main Title */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 bg-gradient-to-r from-white via-gray-200 to-primary-400 bg-clip-text text-transparent animate-slide-up">
            FLASHDOOM
          </h1>

          {/* Subtitle - Original text */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed animate-slide-up">
            The most advanced multi-crypto wallet email marketing platform. Send emails through Binance, Coinbase, Bybit, Trust Wallet, CashApp, PayPal and more trusted wallets.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mb-16 animate-slide-up">
            <Link to="/register" className="px-8 py-4 bg-gradient-to-r from-primary-400 to-primary-600 text-dark-900 font-bold rounded-xl hover:shadow-xl hover:shadow-primary-400/40 transition-all hover:-translate-y-1 text-lg">
              Get Started Free
            </Link>
            <Link to="/login" className="px-8 py-4 border-2 border-primary-400 text-primary-400 font-bold rounded-xl hover:bg-primary-400 hover:text-dark-900 transition-all text-lg">
              Login
            </Link>
          </div>

          {/* Stats - Original numbers */}
          <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto animate-slide-up">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-white/5 border border-white/10 rounded-3xl py-6 px-4 backdrop-blur-sm hover:border-primary-400/50 hover:-translate-y-1 transition-all hover:shadow-lg hover:shadow-primary-400/10">
                <div className="text-3xl sm:text-4xl font-black text-primary-400 drop-shadow-lg">{stat.number}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wallets Section - Original Design */}
      <section className="py-20 bg-gradient-to-b from-transparent via-primary-400/5 to-transparent">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Supported Wallets</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Send emails through trusted crypto wallet domains with industry-leading delivery rates
            </p>
          </div>

          {/* Original 12 Wallets Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {wallets.map((wallet) => (
              <div
                key={wallet.name}
                className="group flex flex-col items-center gap-3 p-5 bg-white/5 border border-white/10 rounded-3xl hover:bg-primary-400/10 hover:border-primary-400/50 hover:-translate-y-2 hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <WalletLogo wallet={wallet} size="md" />
                <span className={`font-semibold ${wallet.textColor} text-sm`}>{wallet.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Original Design */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Why Choose FLASHDOOM?</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Industry-leading features designed for maximum email delivery and engagement
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: BoltIcon, title: 'Lightning Fast', desc: 'Send emails instantly through multiple crypto wallets with our optimized delivery system powered by Cloudflare edge.', emoji: '⚡' },
              { icon: ShieldCheckIcon, title: 'Secure & Private', desc: 'Enterprise-grade security with encrypted communications, anonymous sending, and SOC2 compliant infrastructure.', emoji: '🔒' },
              { icon: ChartBarIcon, title: 'Real-time Analytics', desc: 'Track delivery rates, open rates, click rates, and engagement metrics in a beautiful real-time dashboard.', emoji: '📊' },
              { icon: CheckCircleIcon, title: 'High Delivery Rate', desc: 'Industry-leading 98%+ inbox delivery rates thanks to trusted crypto wallet domains and smart routing.', emoji: '🎯' },
              { icon: GlobeAltIcon, title: 'Global Coverage', desc: 'Reach users worldwide with support for 15+ crypto wallets across 190+ countries and all major regions.', emoji: '🌍' },
              { icon: LockClosedIcon, title: 'Cost Effective', desc: 'Competitive pricing with no hidden fees. Free tier available, pay only for what you use.', emoji: '💰' },
            ].map((feature) => (
              <div key={feature.title} className="group relative bg-gradient-to-br from-white/5 to-white/0 border border-white/10 rounded-3xl p-8 hover:-translate-y-2 transition-all duration-300 hover:border-primary-400/30">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="text-4xl mb-6">{feature.emoji}</div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Original Design */}
      <section className="py-20 bg-gradient-to-r from-primary-400/10 via-primary-400/5 to-primary-400/10 border-y border-primary-400/20">
        <div className="max-w-2xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-gray-400 mb-8">
            Join thousands of marketers using FLASHDOOM to reach their audience through trusted crypto wallets.
          </p>
          <Link to="/register" className="inline-block px-10 py-4 bg-gradient-to-r from-primary-400 to-primary-600 text-dark-900 font-bold rounded-xl hover:shadow-xl hover:shadow-primary-400/40 transition-all hover:-translate-y-1 text-lg">
            Create Free Account
          </Link>
        </div>
      </section>

      {/* Footer - Original with Social Links */}
      <footer className="bg-dark-800 py-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex flex-wrap gap-6">
              <Link to="/" className="text-gray-400 hover:text-primary-400 transition-colors">Home</Link>
              <Link to="/login" className="text-gray-400 hover:text-primary-400 transition-colors">Login</Link>
              <Link to="/register" className="text-gray-400 hover:text-primary-400 transition-colors">Register</Link>
              <a href="mailto:info@flashdoom.com" className="text-gray-400 hover:text-primary-400 transition-colors">Contact</a>
            </div>

            {/* Social Links - Original */}
            <div className="flex gap-3">
              <a href="https://t.me/onetoolz" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:bg-primary-400 hover:border-primary-400 hover:text-dark-900 transition-all hover:-translate-y-1">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.1.154.234.169.333.016.098.035.323.02.498z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/one.toolz" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:bg-primary-400 hover:border-primary-400 hover:text-dark-900 transition-all hover:-translate-y-1">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="https://www.youtube.com/@One_Toolz" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-gray-400 hover:bg-primary-400 hover:border-primary-400 hover:text-dark-900 transition-all hover:-translate-y-1">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="text-center mt-8 pt-8 border-t border-white/5">
            <p className="text-gray-500 text-sm">
              Copyright {new Date().getFullYear()} FLASHDOOM. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
